import React from 'react';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'link'
  | 'on-dark'
  | 'ghost-dark';
export type ButtonSize = 'md' | 'sm';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Primary is the one next step on a screen; use at most one per view. */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shown before the label. Decorative, so mark it aria-hidden. */
  icon?: React.ReactNode;
  /** Shows a spinner and blocks clicks while keeping the button's width. */
  loading?: boolean;
  /** Fill the width of the parent. */
  block?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

/**
 * The class list for a button look, for elements that must not be a <button>,
 * such as a router <Link> or a file-input <label>.
 */
export const buttonClass = (
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  extra?: string,
): string =>
  ['ui-btn', `ui-btn--${variant}`, size === 'sm' ? 'ui-btn--sm' : '', extra || '']
    .filter(Boolean)
    .join(' ');

const Button: React.FC<ButtonProps> = ({
  variant = 'secondary',
  size = 'md',
  icon,
  loading = false,
  block = false,
  className,
  disabled,
  type = 'button',
  children,
  ...rest
}) => (
  <button
    {...rest}
    type={type}
    className={buttonClass(
      variant,
      size,
      [block ? 'ui-btn--block' : '', className || ''].filter(Boolean).join(' '),
    )}
    disabled={disabled || loading}
    aria-busy={loading || undefined}
  >
    {loading ? <span className="ui-spinner" aria-hidden="true" /> : icon}
    {children}
  </button>
);

export default Button;
